import { gql } from '@apollo/client';

export const DIAGNOSIS_FRAGMENT = gql`
  fragment DiagnosisFragment on CarepathDiagnosisType {
    id
    isPrimaryDiagnosis
    emrDiagnosisKey
    isMostRecent
    laterality
    diagnosisCode {
      id
      diagnosisCode
      diagnosisDescription
      tumourStream {
        id
        name
      }
    }
    stageTCodePathological {
      id
      mainCode
      subCode
    }
    stageNCodePathological {
      id
      mainCode
      subCode
    }
    stageMCodePathological {
      id
      mainCode
      subCode
    }
    stageTCodeClinical {
      id
      mainCode
      subCode
    }
    stageNCodeClinical {
      id
      mainCode
      subCode
    }
    stageMCodeClinical {
      id
      mainCode
      subCode
    }
    morphology {
      id
      description
      code
    }
    diagnosisDate
    ajccMajorVersion
    practitioner {
      id
      prefix
      firstName
      lastName
    }
  }
`;
export const GET_PATIENT_PROFILE = gql`
  ${DIAGNOSIS_FRAGMENT}
  query patient($patientId: ID!) {
    patient(id: $patientId) {
      id
      firstName
      lastName
      middleName
      fullName
      preferredName
      email
      pxOptedIn
      pxOptOutComment
      horizonCenterId
      ida
      dob
      patientState
      gpFullName
      patientGender
      primaryPhone
      secondaryPhone
      healthMedicareNumber
      healthMedicareIrn
      medicareExpiryRawYear
      medicareExpiryRawMonth
      policyNumber
      insurances {
        id
        policyNumber
        payor {
          id
          name
        }
        expiryDate
      }
      healthPrivateHealthFundName
      userProfile {
        id
        registrationAccessType
        showDistressThermometer
        hasPatId1
        patientFeatures {
          distressThermometerEotEnabled
          distressThermometerEnabled
          managementPlansEnabled
          covidEnabled
        }
      }
      emergencyContact {
        id
        firstName
        lastName
        relationship
        homePhoneNumber
        mobilePhoneNumber
      }
      nextOfKinContact {
        id
        firstName
        lastName
        relationship
        homePhoneNumber
        mobilePhoneNumber
      }
      address {
        id
        line1
        line2
        city
        state
        country
        postcode
        formattedAddress
        ukFormattedAddress
      }
      allergy {
        id
        name
        reaction
        severity
        status
      }
      appointments {
        id
        startTime
        description
        shortDescription
        duration
        location {
          id
          name
        }
        department {
          id
          alias
        }
      }
      allReferrers {
        id
        relationshipType
        practitioner {
          id
          qualification
          firstName
          lastName
          phone
        }
      }
      carepathPatientDiagnosis {
        ...DiagnosisFragment
        relatedPrimaryDiagnosis {
          ...DiagnosisFragment
        }
        carepathbaselinebreastSet {
          id
          grade
          er
          pgr
          her2
          mastectomy
          reconstruction
          excerciseReferral
          surgery
          oncoplasticSurgery
          reconstructionTimeline
          reconstructionType
          expandersInSitu
          lymphovascularInvasion
          lymphovascularInvasionExtent
          radialMarginStatus
          neoadjuvantSystemicTherapy
          nstAdditionalInfo
          exerciseDiscussedWithPatient
        }
        carepathbaselinelungSet {
          id
          weightLoss
          smokingHistory
          egfr
          alk
          pdl1
        }
        carepathbaselineprostateSet {
          id
          gleasonOne
          gleasonTwo
          isupGrade
          psa
          psaDate
          psma
          positiveSite
          prostatectomy
        }
        carepathbaselinelymphomaSet {
          id
          stage
          aOrBSymptoms
          extranodal
          bulkDisease
        }
        carepathbaselineheadandneckSet {
          id
          p16
          smokingHistory
          stageTCode
          stageNCode
          stageMCode
          cigarettesPerDay
          yearsSmoked
          packYears
          dateOfSurgery
          extracapsularExtension
          margins
          marginsMeasurement
          periNeuralInvasion
          pniType
          patientDiscussedAtMdt
          treatmentConsistentMdt
        }
      }
      tags {
        id
        tag {
          id
          name
        }
      }
      emrPatientId
      emrInstance
    }
    getRecentPatients {
      id
      emrInstance
    }
  }
`;

// Basic diagnosis fragment without staging/baseline sets for RO context
export const BASIC_DIAGNOSIS_FRAGMENT = gql`
  fragment BasicDiagnosisFragment on CarepathDiagnosisType {
    id
    isPrimaryDiagnosis
    emrDiagnosisKey
    isMostRecent
    laterality
    diagnosisDate
    diagnosisCode {
      id
      diagnosisCode
      diagnosisDescription
      tumourStream {
        id
        name
      }
    }
  }
`;

// Minimal patient query for RO context - used for patient card header
export const GET_RO_PATIENT_CONTEXT = gql`
  ${BASIC_DIAGNOSIS_FRAGMENT}
  query roPatientContext($patientId: ID!) {
    patient(id: $patientId) {
      id
      firstName
      lastName
      middleName
      fullName
      preferredName
      ida
      dob
      patientGender
      primaryPhone
      secondaryPhone
      email
      emrPatientId
      emrInstance
      horizonCenterId
      pxOptedIn
      pxOptOutComment
      address {
        id
        line1
        line2
        city
        state
        country
        postcode
        formattedAddress
        ukFormattedAddress
      }
      allergy {
        id
        name
        reaction
        severity
        status
      }
      allReferrers {
        id
        relationshipType
        practitioner {
          id
          firstName
          lastName
          qualification
          phone
        }
      }
      carepathPatientDiagnosis {
        ...BasicDiagnosisFragment
      }
      tags {
        id
        tag {
          id
          name
        }
      }
      userProfile {
        id
        hasPatId1
        registrationAccessType
      }
    }
  }
`;

// Full patient details query for RO "More Details" panel - lazy loaded on expand
export const GET_RO_PATIENT_MORE_DETAILS = gql`
  ${DIAGNOSIS_FRAGMENT}
  query roPatientMoreDetails($patientId: ID!) {
    patient(id: $patientId) {
      id
      healthMedicareNumber
      healthMedicareIrn
      medicareExpiryRawYear
      medicareExpiryRawMonth
      policyNumber
      insurances {
        id
        policyNumber
        payor {
          id
          name
        }
        expiryDate
      }
      emergencyContact {
        id
        firstName
        lastName
        relationship
        homePhoneNumber
        mobilePhoneNumber
      }
      nextOfKinContact {
        id
        firstName
        lastName
        relationship
        homePhoneNumber
        mobilePhoneNumber
      }
      appointments {
        id
        startTime
        description
        shortDescription
        duration
        location {
          id
          name
        }
        department {
          id
          alias
        }
      }
      carepathPatientDiagnosis {
        ...DiagnosisFragment
        relatedPrimaryDiagnosis {
          ...DiagnosisFragment
        }
        carepathbaselinebreastSet {
          id
          grade
          er
          pgr
          her2
          mastectomy
          reconstruction
          excerciseReferral
          surgery
          oncoplasticSurgery
          reconstructionTimeline
          reconstructionType
          expandersInSitu
          lymphovascularInvasion
          lymphovascularInvasionExtent
          radialMarginStatus
          neoadjuvantSystemicTherapy
          nstAdditionalInfo
          exerciseDiscussedWithPatient
        }
        carepathbaselinelungSet {
          id
          weightLoss
          smokingHistory
          egfr
          alk
          pdl1
        }
        carepathbaselineprostateSet {
          id
          gleasonOne
          gleasonTwo
          isupGrade
          psa
          psaDate
          psma
          positiveSite
          prostatectomy
        }
        carepathbaselinelymphomaSet {
          id
          stage
          aOrBSymptoms
          extranodal
          bulkDisease
        }
        carepathbaselineheadandneckSet {
          id
          p16
          smokingHistory
          stageTCode
          stageNCode
          stageMCode
          cigarettesPerDay
          yearsSmoked
          packYears
          dateOfSurgery
          extracapsularExtension
          margins
          marginsMeasurement
          periNeuralInvasion
          pniType
          patientDiscussedAtMdt
          treatmentConsistentMdt
        }
      }
    }
  }
`;

export const GET_PATIENT_PHOTO = gql`
  query GetPatientPhoto($patientId: ID!) {
    patientPhoto(id: $patientId) {
      id
      photoUrl
    }
  }
`;

export const GET_LOCATION_PROFILE = gql`
  query GetLocationProfile($patientId: ID!) {
    locationsPsoSystem(patientId: $patientId) {
      id
      alias
    }
  }
`;

export const GET_ALERTS = gql`
  query alerts($patientId: ID!) {
    alerts(patientId: $patientId) {
      id
      name
      comment
      status
      assigned
    }
  }
`;

export const UPDATE_CENTER_MUTATION = gql`
  mutation updateHorizonCenter($id: ID!, $horizonCenterId: ID) {
    updateHorizonCenter(id: $id, horizonCenterId: $horizonCenterId) {
      horizonCenterId
      patient {
        id
        horizonCenterId
      }
      user {
        id
      }
    }
  }
`;

export const UPDATE_PATIENT_OPT_IN = gql`
  mutation UpdatePatient(
    $id: ID!
    $pxOptedIn: Boolean
    $email: String
    $primaryPhone: String
    $pxOptOutComment: String
  ) {
    updatePatient(
      id: $id
      pxOptedIn: $pxOptedIn
      email: $email
      primaryPhone: $primaryPhone
      pxOptOutComment: $pxOptOutComment
    ) {
      patient {
        id
        pxOptedIn
        email
        primaryPhone
        pxOptOutComment
      }
    }
  }
`;

export const GET_PATIENT_CREATED_AT = gql`
  query patientCreatedAt($patientId: ID!) {
    patient(id: $patientId) {
      id
      createdAt
    }
  }
`;

export const GET_TAG_OPTIONS = gql`
  query tagOptions {
    tagOptions {
      id
      name
    }
  }
`;

export const UPDATE_PATIENT_TAGS = gql`
  mutation updatePatientTagItems($patientId: ID!, $updatedTags: [String]!) {
    updatePatientTagItems(patientId: $patientId, updatedTags: $updatedTags) {
      tagItems {
        id
        tag {
          id
          name
        }
      }
    }
  }
`;
